import React, { useEffect, useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    StatusBar,
    Platform,
    ActivityIndicator,
    Share,
    Dimensions,
} from 'react-native';
import Pdf from 'react-native-pdf';
import ReactNativeBlobUtil from 'react-native-blob-util';
import { v2GetDocumentDownloadUrl } from '../services/v2/api';
import type { V2Document } from '../types/v2';
import { useAuth } from '../contexts/AuthContext';
import BackButton from '../components/UIComponents/BackButton';
import { COLORS } from '../theme/themeTokens';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

interface PDFPreviewScreenProps {
    document: V2Document;
    onClose: () => void;
}

const PDFPreviewScreen: React.FC<PDFPreviewScreenProps> = ({
    document,
    onClose,
}) => {
    // Bumped iOS to 60 — see LocalPDFPreviewScreen for the same
    // reasoning (overlay sits outside SafeAreaView).
    const statusBarHeight = Platform.OS === 'android' ? 28 : 60;
    const { handleSessionExpired } = useAuth();
    const [localPath, setLocalPath] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);

    // Check if error is authentication related
    const isAuthError = (errorMessage: string): boolean => {
        const authErrors = [
            'Not authenticated',
            'Session expired',
            'Unauthorized',
            'Firebase',
            'authentication',
            'token',
        ];
        return authErrors.some(keyword =>
            errorMessage.toLowerCase().includes(keyword.toLowerCase())
        );
    };

    // Download PDF to cache
    const downloadPDF = useCallback(async () => {
        try {
            setLoading(true);
            setDownloading(true);
            setError(null);
            setDownloadProgress(0);

            // Get presigned download URL
            const response = await v2GetDocumentDownloadUrl(document.id);

            if (!response.download_url) {
                throw new Error('No download URL received');
            }

            // Generate cache file path
            const cacheDir = ReactNativeBlobUtil.fs.dirs.CacheDir;
            const fileName = document.filename || `report_${document.id}.pdf`;
            const filePath = `${cacheDir}/${fileName}`;

            // Download file
            const downloadResponse = await ReactNativeBlobUtil.config({
                path: filePath,
                fileCache: true,
            })
                .fetch('GET', response.download_url)
                .progress((received, total) => {
                    const progress = Math.round((received / total) * 100);
                    setDownloadProgress(progress);
                });

            const path = downloadResponse.path();
            setLocalPath(path);
            setDownloading(false);
        } catch (err) {
            console.error('Failed to download PDF:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to download PDF';

            // Check if this is an auth error - redirect to login
            if (isAuthError(errorMessage)) {
                onClose(); // Close PDF preview first
                handleSessionExpired(errorMessage);
                return;
            }

            setError(errorMessage);
            setDownloading(false);
        } finally {
            setLoading(false);
        }
    }, [document.id, document.filename, handleSessionExpired, onClose]);

    useEffect(() => {
        downloadPDF();

        // Cleanup: remove cached file on unmount
        return () => {
            if (localPath) {
                ReactNativeBlobUtil.fs.unlink(localPath).catch(() => {
                    // Ignore cleanup errors
                });
            }
        };
    }, [downloadPDF]);

    // Handle share
    const handleShare = async () => {
        if (!localPath) return;

        try {
            if (Platform.OS === 'ios') {
                await Share.share({
                    url: `file://${localPath}`,
                });
            } else {
                // For Android, use the file path
                await Share.share({
                    message: `${document.filename}`,
                    title: document.filename,
                });
            }
        } catch (err) {
            console.error('Share failed:', err);
        }
    };

    // Format filename for display
    const displayName = document.filename
        ? document.filename.replace(/\.[^/.]+$/, '')
        : `Report ${document.report_id}`;

    return (
        <View style={styles.container}>
            <StatusBar barStyle="light-content" backgroundColor={COLORS.deepNavy} />

            {/* Header — chevron AND title share the same TouchableOpacity
                so the whole left/center area is tappable to close. The
                right-side share button stays separate. */}
            <View style={[styles.header, { paddingTop: statusBarHeight }]}>
                <TouchableOpacity
                    style={styles.headerTapZone}
                    onPress={onClose}
                    activeOpacity={0.7}
                >
                    <BackButton onPress={onClose} iconVariant="white" showLabel={false} style={{ marginBottom: 0 }} />
                    <View style={styles.headerTitleContainer}>
                        <Text style={styles.headerTitle} numberOfLines={1}>
                            {displayName}
                        </Text>
                        {/* Page indicator absolutely positioned BELOW the
                            title so the title row stays single-line and
                            the title text vertically aligns with the
                            chevron icon (via alignItems: 'center' on
                            parent row). */}
                        {totalPages > 0 && (
                            <Text style={styles.pageIndicator}>
                                Page {currentPage} of {totalPages}
                            </Text>
                        )}
                    </View>
                </TouchableOpacity>
                <TouchableOpacity
                    onPress={handleShare}
                    style={styles.shareButton}
                    disabled={!localPath}>
                    <Text style={[styles.shareButtonText, !localPath && styles.disabled]}>
                        ↗
                    </Text>
                </TouchableOpacity>
            </View>

            {/* Content */}
            <View style={styles.content}>
                {loading ? (
                    <View style={styles.loadingContainer}>
                        <ActivityIndicator size="large" color={COLORS.brandOrange} />
                        {downloading && (
                            <Text style={styles.loadingText}>
                                Downloading... {downloadProgress}%
                            </Text>
                        )}
                    </View>
                ) : error ? (
                    <View style={styles.errorContainer}>
                        <Text style={styles.errorText}>{error}</Text>
                        <TouchableOpacity style={styles.retryButton} onPress={downloadPDF}>
                            <Text style={styles.retryButtonText}>Retry</Text>
                        </TouchableOpacity>
                    </View>
                ) : localPath ? (
                    <Pdf
                        source={{ uri: localPath }}
                        style={styles.pdf}
                        onLoadComplete={(numberOfPages) => {
                            setTotalPages(numberOfPages);
                        }}
                        onPageChanged={(page) => {
                            setCurrentPage(page);
                        }}
                        onError={(err) => {
                            console.error('PDF render error:', err);
                            setError('Failed to render PDF');
                        }}
                        enablePaging={true}
                        horizontal={false}
                        fitPolicy={0}
                        spacing={10}
                    />
                ) : null}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.deepNavy,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingBottom: 16,
        backgroundColor: COLORS.deepNavy,
    },
    headerTapZone: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
    },
    // Title sits on the chevron's vertical mid-line. The
    // page-indicator (when present) overlays absolutely below the
    // title so the container's intrinsic height stays = the title
    // height alone — `alignItems: 'center'` on the parent header
    // then aligns the title text with the chevron icon centre.
    headerTitleContainer: {
        flex: 1,
        alignItems: 'center',
        position: 'relative',
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: '600',
        lineHeight: 24,
        color: COLORS.white,
    },
    pageIndicator: {
        position: 'absolute',
        top: 26,            // title.lineHeight (24) + 2 dp gap
        fontSize: 14,
        lineHeight: 18,
        color: COLORS.blueSteel,
    },
    shareButton: {
        width: 40,
        height: 40,
        justifyContent: 'center',
        alignItems: 'center',
    },
    shareButtonText: {
        fontSize: 26,
        color: COLORS.white,
        fontWeight: '600',
    },
    disabled: {
        opacity: 0.5,
    },
    content: {
        flex: 1,
        backgroundColor: COLORS.pureLight,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        gap: 16,
    },
    loadingText: {
        fontSize: 18,
        color: COLORS.steelNavy,
    },
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
        gap: 16,
    },
    errorText: {
        fontSize: 18,
        color: COLORS.brandOrange,
        textAlign: 'center',
    },
    retryButton: {
        paddingHorizontal: 24,
        paddingVertical: 12,
        backgroundColor: COLORS.brandOrange,
        borderRadius: 8,
    },
    retryButtonText: {
      fontFamily: 'Inter',
      lineHeight: 22,
      fontSize: 16,
        color: COLORS.white,
      fontWeight: '500',
    },
    pdf: {
        flex: 1,
        width: SCREEN_WIDTH,
        height: SCREEN_HEIGHT,
        backgroundColor: COLORS.pureLight,
    },
});

export default PDFPreviewScreen;

